import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CreateCircuit.css';

const { BaseLayer, Overlay } = LayersControl;

// Fix assets Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const WS_URL = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? `wss://${window.location.hostname}/ws` : `ws://${window.location.hostname}:5001`);

/* ============ ICONS ============ */
const Svg = ({ children, size = 16, className = '', stroke = 'currentColor', strokeWidth = 2, ...rest }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={className} aria-hidden="true" focusable="false" {...rest}>
    {children}
  </svg>
);

const IconBack = (props) => (
  <Svg {...props}><polyline points="15 18 9 12 15 6" /></Svg>
);

const IconPlay = (props) => (
  <Svg {...props}><polygon points="5 3 19 12 5 21 5 3" /></Svg>
);

const IconStop = (props) => (
  <Svg {...props}><rect x="6" y="6" width="12" height="12" /></Svg>
);

const IconRefresh = (props) => (
  <Svg {...props}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </Svg>
);

const IconSave = (props) => (
  <Svg {...props}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </Svg>
);

const IconCheck = (props) => (
  <Svg {...props}><polyline points="20 6 9 17 4 12" /></Svg>
);

const IconTarget = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </Svg>
);

const IconDevice = (props) => (
  <Svg {...props}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </Svg>
);

/* ============ MAP COMPONENTS ============ */

// Segui ultimo punto
function FollowLast({ follow, lastPoint }) {
  const map = useMap();
  useEffect(() => {
    if (follow && lastPoint) {
      map.setView([lastPoint.lat, lastPoint.lon], Math.max(map.getZoom(), 18), { animate: true });
    }
  }, [follow, lastPoint, map]);
  return null;
}

// Centra mappa sul primo punto
function FitOnFirstPoint({ points }) {
  const map = useMap();
  const firstFit = useRef(false);
  useEffect(() => {
    if (!firstFit.current && points.length > 0) {
      const last = points[points.length - 1];
      map.setView([last.lat, last.lon], 18, { animate: true });
      firstFit.current = true;
    }
  }, [points, map]);
  return null;
}

// Icona device colorata
const makeDeviceIcon = (hexColor = '#0ea5e9') => L.divIcon({
  className: 'device-dot',
  html: `<span style="display:inline-block;width:16px;height:16px;background:${hexColor};
         border:3px solid white;border-radius:50%;box-shadow:0 0 6px rgba(0,0,0,.4)"></span>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8]
});

/* ============ PALETTE COLORI ============ */
const PALETTE = [
  '#e10600', '#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899',
  '#14b8a6', '#f97316', '#6366f1', '#84cc16'
];

/* ============ MAIN COMPONENT ============ */
export default function CreateCircuit({ apiBase = API_BASE }) {
  const navigate = useNavigate();

  // Stato tracciamento
  const [tracking, setTracking] = useState(false);
  const [points, setPoints] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [circuitLength, setCircuitLength] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Device
  const [detectedDevices, setDetectedDevices] = useState(new Map()); // MAC -> lastPkt
  const [selectedDevice, setSelectedDevice] = useState(null);

  // UI
  const [follow, setFollow] = useState(true);
  const [trackName, setTrackName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Parametri inizializzazione
  const [spacingMeters, setSpacingMeters] = useState(1);
  const [minQual, setMinQual] = useState(0);
  const [autoClose, setAutoClose] = useState(true);
  const [autoCloseApplied, setAutoCloseApplied] = useState(false);

  // WebSocket ref
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Refs per dati ad alta frequenza (evita re-render continui)
  const pointsBufferRef = useRef([]);
  const devicesBufferRef = useRef(new Map());
  const renderThrottleRef = useRef(null);
  const RENDER_INTERVAL_MS = 100; // Aggiorna UI ogni 100ms invece che 15 volte/sec

  // Colori device
  const deviceColorMap = useRef(new Map());
  const colorIndexRef = useRef(0);

  const getDeviceColor = useCallback((mac) => {
    if (!mac) return '#888';
    const key = String(mac).toUpperCase();
    if (!deviceColorMap.current.has(key)) {
      const color = PALETTE[colorIndexRef.current % PALETTE.length];
      deviceColorMap.current.set(key, color);
      colorIndexRef.current += 1;
    }
    return deviceColorMap.current.get(key);
  }, []);

  // Funzione per schedulare aggiornamento UI (throttled)
  const scheduleRenderUpdate = useCallback(() => {
    if (renderThrottleRef.current) return; // Già schedulato

    renderThrottleRef.current = setTimeout(() => {
      renderThrottleRef.current = null;

      // Aggiorna devices se ci sono novità
      if (devicesBufferRef.current.size > 0) {
        setDetectedDevices(prev => {
          const next = new Map(prev);
          devicesBufferRef.current.forEach((val, key) => next.set(key, val));
          return next;
        });
      }

      // Aggiorna points se ci sono novità
      if (pointsBufferRef.current.length > 0) {
        setPoints(pointsBufferRef.current.slice());
      }
    }, RENDER_INTERVAL_MS);
  }, []);

  // Connessione WebSocket
  useEffect(() => {
    let alive = true;

    const connect = () => {
      if (!alive) return;

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected to', WS_URL);
        setWsConnected(true);
      };

      ws.onclose = () => {
        console.log('[WS] Disconnected');
        setWsConnected(false);
        if (alive) {
          reconnectTimeoutRef.current = setTimeout(connect, 2000);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          // Pacchetto GPS raw (quando non c'è gara attiva)
          if (msg.type === 'gps_raw' && msg.data) {
            const pkt = msg.data;
            const mac = String(pkt.mac || '').toUpperCase();
            if (mac) {
              // Accumula nel buffer invece di aggiornare lo state direttamente
              devicesBufferRef.current.set(mac, { ...pkt, receivedAt: Date.now() });
              scheduleRenderUpdate();
            }
          }

          // Pacchetto track builder
          if (msg.type === 'track_builder_point' && msg.data) {
            const pkt = msg.data;
            // Accumula nel buffer invece di aggiornare lo state direttamente
            pointsBufferRef.current.push(pkt);
            if (pointsBufferRef.current.length > 50000) {
              pointsBufferRef.current = pointsBufferRef.current.slice(-50000);
            }
            scheduleRenderUpdate();
          }

        } catch (e) {
          console.warn('[WS] Parse error:', e);
        }
      };
    };

    connect();

    return () => {
      alive = false;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (renderThrottleRef.current) clearTimeout(renderThrottleRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [scheduleRenderUpdate]);

  // Caricamento stato iniziale
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/track-builder/health`);
        if (res.ok) {
          const data = await res.json();
          setTracking(data.trackingActive);
          setSelectedDevice(data.selectedDevice);
        }

        const pathRes = await fetch(`${apiBase}/api/track-builder/path`);
        if (pathRes.ok) {
          const pts = await pathRes.json();
          if (Array.isArray(pts)) {
            pointsBufferRef.current = pts;
            setPoints(pts);
          }
        }

        const sectorsRes = await fetch(`${apiBase}/api/track-builder/sectors`);
        if (sectorsRes.ok) {
          const data = await sectorsRes.json();
          if (Array.isArray(data.sectors)) setSectors(data.sectors);
        }
      } catch (e) {
        console.error('[INIT] Error:', e);
      }
    })();
  }, [apiBase]);

  // Handlers
  const handleStartTracking = async () => {
    try {
      const res = await fetch(`${apiBase}/api/track-builder/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceMac: selectedDevice })
      });
      if (res.ok) {
        const data = await res.json();
        setTracking(data.trackingActive);
      }
    } catch (e) {
      console.error('[START] Error:', e);
    }
  };

  const handleStopTracking = async () => {
    try {
      const res = await fetch(`${apiBase}/api/track-builder/stop`, { method: 'POST' });
      if (res.ok) setTracking(false);
    } catch (e) {
      console.error('[STOP] Error:', e);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Vuoi resettare il tracciato corrente?')) return;
    try {
      const res = await fetch(`${apiBase}/api/track-builder/reset`, { method: 'POST' });
      if (res.ok) {
        pointsBufferRef.current = [];
        setPoints([]);
        setSectors([]);
        setCircuitLength(null);
        setTracking(false);
        setSaveSuccess(false);
        setSaveError('');
      }
    } catch (e) {
      console.error('[RESET] Error:', e);
    }
  };

  const handleSelectDevice = async (mac) => {
    const newMac = mac === selectedDevice ? null : mac;
    setSelectedDevice(newMac);
    try {
      await fetch(`${apiBase}/api/track-builder/device`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mac: newMac })
      });
    } catch (e) {
      console.error('[DEVICE] Error:', e);
    }
  };

  const handleInitSectors = async () => {
    try {
      const res = await fetch(`${apiBase}/api/track-builder/init-sectors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spacingMeters, minQual, autoClose })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sectors) setSectors(data.sectors);
        if (data.stats?.lengthMeters) setCircuitLength(data.stats.lengthMeters);
        setAutoCloseApplied(!!data.stats?.autoCloseApplied);
      }
    } catch (e) {
      console.error('[INIT-SECTORS] Error:', e);
    }
  };

  const handleSave = async () => {
    if (!trackName.trim()) {
      setSaveError('Inserisci un nome per il circuito');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const res = await fetch(`${apiBase}/api/track-builder/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trackName.trim(), autoClose })
      });
      const data = await res.json();
      if (data.ok) {
        setSaveSuccess(true);
        setTimeout(() => navigate('/circuiti'), 1500);
      } else {
        setSaveError(data.error || 'Errore nel salvataggio');
      }
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Dati mappa
  const polyline = useMemo(() => points.map(p => [p.lat, p.lon]), [points]);
  const lastPoint = points.length ? points[points.length - 1] : null;
  const deviceList = useMemo(() => Array.from(detectedDevices.entries()), [detectedDevices]);

  // Centro mappa
  const defaultCenter = [44.652510206952385, 10.781544484815864];
  const mapCenter = lastPoint ? [lastPoint.lat, lastPoint.lon] : defaultCenter;

  return (
    <div className="create-circuit-page">
      {/* HEADER */}
      <div className="create-circuit-header">
        <div className="create-circuit-header-bg" />
        <div className="create-circuit-header-content">
          <button className="create-circuit-back" onClick={() => navigate('/circuiti')}>
            <IconBack size={20} />
            <span>Circuiti</span>
          </button>
          <div className="create-circuit-title">
            <h1>Crea Nuovo Circuito</h1>
            <p>Traccia il percorso con un dispositivo GPS</p>
          </div>
          <div className="create-circuit-status">
            <span className={`status-dot ${wsConnected ? 'connected' : 'disconnected'}`} />
            <span>{wsConnected ? 'Connesso' : 'Disconnesso'}</span>
          </div>
        </div>
      </div>

      <div className="create-circuit-main">
        {/* SIDEBAR */}
        <aside className="create-circuit-sidebar">
          {/* Device Selection */}
          <section className="sidebar-section">
            <h3><IconDevice size={18} /> Dispositivi Rilevati</h3>
            <div className="device-list">
              {deviceList.length === 0 ? (
                <p className="device-empty">In attesa di pacchetti GPS...</p>
              ) : (
                deviceList.map(([mac, pkt]) => (
                  <button
                    key={mac}
                    className={`device-item ${selectedDevice === mac ? 'selected' : ''}`}
                    onClick={() => handleSelectDevice(mac)}
                  >
                    <span className="device-color" style={{ background: getDeviceColor(mac) }} />
                    <div className="device-info">
                      <span className="device-mac">{mac}</span>
                      <span className="device-meta">
                        {pkt.speedKmh?.toFixed(1) || '0'} km/h | {pkt.sats || 0} sats
                      </span>
                    </div>
                    {selectedDevice === mac && <IconCheck size={16} className="device-check" />}
                  </button>
                ))
              )}
            </div>
            {deviceList.length > 0 && !selectedDevice && (
              <p className="device-hint">Seleziona un device per filtrare il tracciamento</p>
            )}
          </section>

          {/* Tracking Controls */}
          <section className="sidebar-section">
            <h3><IconTarget size={18} /> Tracciamento</h3>
            <div className="control-buttons">
              {!tracking ? (
                <button className="btn btn-primary" onClick={handleStartTracking}>
                  <IconPlay size={18} />
                  <span>Avvia Tracciamento</span>
                </button>
              ) : (
                <button className="btn btn-stop" onClick={handleStopTracking}>
                  <IconStop size={18} />
                  <span>Ferma Tracciamento</span>
                </button>
              )}
              <button className="btn btn-ghost" onClick={handleReset}>
                <IconRefresh size={18} />
                <span>Reset</span>
              </button>
            </div>

            <div className="tracking-stats">
              <div className="stat">
                <span className="stat-value">{points.length}</span>
                <span className="stat-label">Punti</span>
              </div>
              <div className="stat">
                <span className="stat-value">{sectors.length}</span>
                <span className="stat-label">Settori</span>
              </div>
              <div className="stat">
                <span className="stat-value">
                  {circuitLength ? `${circuitLength.toFixed(0)}m` : '-'}
                </span>
                <span className="stat-label">Lunghezza</span>
              </div>
            </div>

            <label className="follow-toggle">
              <input type="checkbox" checked={follow} onChange={e => setFollow(e.target.checked)} />
              <span className="toggle-slider" />
              <span className="toggle-label">Segui posizione</span>
            </label>
          </section>

          {/* Initialize Circuit */}
          <section className="sidebar-section">
            <h3>Parametri Circuito</h3>
            <div className="param-fields">
              <div className="param-field">
                <label>Spaziatura settori (m)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={spacingMeters}
                  onChange={e => setSpacingMeters(Number(e.target.value))}
                />
              </div>
              <div className="param-field">
                <label>Qualita minima (0-9)</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="9"
                  value={minQual}
                  onChange={e => setMinQual(Number(e.target.value))}
                />
              </div>
            </div>
            <label className="follow-toggle" style={{ marginBottom: 10 }}>
              <input type="checkbox" checked={autoClose} onChange={e => setAutoClose(e.target.checked)} />
              <span className="toggle-slider" />
              <span className="toggle-label">Chiudi tracciato automaticamente</span>
            </label>
            {autoClose && (
              <p className="param-hint">
                Se il punto finale è vicino a quello iniziale ({'<'}50m), il tracciato viene chiuso automaticamente: i punti in eccesso vengono rimossi e il punto finale viene allineato esattamente al punto di partenza.
                {autoCloseApplied && <span className="autoclose-badge"> Correzione applicata</span>}
              </p>
            )}
            <button
              className="btn btn-secondary"
              onClick={handleInitSectors}
              disabled={points.length < 2}
            >
              Inizializza Circuito per Classifica
            </button>
          </section>

          {/* Save */}
          <section className="sidebar-section save-section">
            <h3><IconSave size={18} /> Salva Circuito</h3>
            <div className="save-form">
              <input
                type="text"
                placeholder="Nome del circuito..."
                value={trackName}
                onChange={e => setTrackName(e.target.value)}
                disabled={saving || saveSuccess}
              />
              <button
                className={`btn btn-primary ${saveSuccess ? 'success' : ''}`}
                onClick={handleSave}
                disabled={saving || saveSuccess || points.length < 2}
              >
                {saving ? (
                  <><span className="btn-spinner" /> Salvataggio...</>
                ) : saveSuccess ? (
                  <><IconCheck size={18} /> Salvato!</>
                ) : (
                  <><IconSave size={18} /> Salva Circuito</>
                )}
              </button>
              {saveError && <p className="save-error">{saveError}</p>}
            </div>
          </section>
        </aside>

        {/* MAP */}
        <div className="create-circuit-map-container">
          <MapContainer
            className="create-circuit-map"
            center={mapCenter}
            zoom={18}
            maxZoom={22}
            scrollWheelZoom
            zoomSnap={0.25}
          >
            <LayersControl position="topleft">
              <BaseLayer checked name="Mappa (OSM)">
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxNativeZoom={19}
                  maxZoom={22}
                />
              </BaseLayer>
              <BaseLayer name="Satellite (Esri)">
                <TileLayer
                  attribution="Imagery &copy; Esri"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxNativeZoom={19}
                  maxZoom={22}
                />
              </BaseLayer>
            </LayersControl>

            <FitOnFirstPoint points={points} />
            <FollowLast follow={follow} lastPoint={lastPoint} />

            {/* Tracciato */}
            {polyline.length > 1 && (
              <Polyline
                positions={polyline}
                pathOptions={{ color: '#22c55e', opacity: 0.9, weight: 4 }}
              />
            )}

            {/* Settori */}
            {sectors.map((s) => (
              <CircleMarker
                key={s.idx}
                center={[s.lat, s.lon]}
                radius={3}
                pathOptions={{ color: '#000', fillColor: '#000', fillOpacity: 1, opacity: 1, weight: 1 }}
              />
            ))}

            {/* Device markers */}
            {deviceList.map(([mac, pkt]) => (
              <Marker
                key={mac}
                position={[pkt.lat, pkt.lon]}
                icon={makeDeviceIcon(getDeviceColor(mac))}
              />
            ))}

            {/* Punto start/finish */}
            {polyline.length > 0 && (
              <CircleMarker
                center={polyline[0]}
                radius={8}
                pathOptions={{ color: '#fff', fillColor: '#e10600', fillOpacity: 1, opacity: 1, weight: 3 }}
              />
            )}
          </MapContainer>

          {/* Map overlay info */}
          <div className="map-overlay-info">
            <span className={`tracking-badge ${tracking ? 'active' : ''}`}>
              {tracking ? 'TRACCIAMENTO ATTIVO' : 'TRACCIAMENTO FERMO'}
            </span>
            {selectedDevice && (
              <span className="device-badge">
                Device: {selectedDevice}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
